import { TableCell as MUITableCell, makeStyles } from "@material-ui/core";
import classNames from "classnames";
import dayjs from "dayjs";
import React, { useCallback, useMemo } from "react";
import type {
  EditedRowProps,
  TableColumnDefinition,
  TableProps,
  TableRowDefinition,
} from "./types";
import {
  computeColumnWidth,
  mapAlignmentToTextAlign,
  mapAlignmentToVerticalAlign,
} from "./util";

interface TableCellProps {
  row: TableRowDefinition;
  column: TableColumnDefinition;
  index: number;
  cellIndex: number;
  className?: string;
  children?: JSX.Element[] | JSX.Element;
  isGroupHeaderCell?: boolean;
}

interface StyleProps {
  padding: string;
  cellIndex: number;
}

const useStyles = makeStyles((theme) => ({
  cell: {
    padding: (props: StyleProps) => (props.padding ? props.padding : null),
    boxSizing: "border-box",
    borderBottom: "revert",
    "&:first-child": {
      paddingLeft: theme.spacing(),
    },
    "&:last-child": {
      paddingRight: theme.spacing(),
    },
  },
  stickyCell: {
    position: "sticky",
    left: (props: StyleProps) => (props.cellIndex === 0 ? 0 : "auto"),
    zIndex: 1,
    // Inherit the row's background so hover / `Mui-selected` / custom
    // `rowClass` styles apply visibly to the sticky column. The row itself
    // carries an opaque default — see TableRow.tsx — so this stays opaque
    // over scrolled cells.
    backgroundColor: "inherit",
  },
  stickyHeaderCell: {
    zIndex: 2,
  },
  lastStickyCell: {
    boxShadow: "var(--sticky-shadow, none)",
    clipPath: "inset(0 -10px 0 0)",
  },
}));

const TableCell = ({
  row,
  column,
  index,
  cellIndex,
  classes,
  className,
  children,
  editing,
  editedRow,
  setEditedRow,
  isGroupHeaderCell,
  columns,
  minTableWidth,
  columnTotals,
}: TableCellProps & TableProps & EditedRowProps) => {
  const cellClasses = useStyles({ padding: column.padding, cellIndex });

  // `cellIndex` reflects the cell's position in the *rendered* row, which
  // doesn't always match its position in the full `columns` array — e.g.
  // `GroupedTableRow` slices off the expander column before mapping.
  // Sticky offsets need the canonical column index, so derive it from the
  // column reference (with `cellIndex` as a fallback for callers that pass
  // a column not in the array).
  const columnIndex = useMemo(() => {
    const idx = columns.indexOf(column);
    return idx >= 0 ? idx : cellIndex;
  }, [columns, column, cellIndex]);

  const getStickyLeft = useCallback(() => {
    if (!column.isSticky) return "auto";
    let left = 0;
    for (let i = 0; i < columnIndex; i++) {
      if (columns[i].isSticky) {
        left += columns[i].width || 0;
      }
    }
    return left;
  }, [column.isSticky, columnIndex, columns]);

  const isLastSticky = useCallback(() => {
    return (
      column.isSticky &&
      (columnIndex === columns.length - 1 || !columns[columnIndex + 1].isSticky)
    );
  }, [column.isSticky, columnIndex, columns]);

  const renderEditCell = useCallback((): JSX.Element | string => {
    return React.cloneElement(
      column.editCell(editedRow[column.field], index, column),
      {
        onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
          if (dayjs(event as unknown as dayjs.Dayjs).isValid())
            editedRow[column.field] = (
              event as unknown as dayjs.Dayjs
            ).toISOString();
          else
            editedRow[column.field] =
              (!!event.target.value && event.target.value) ||
              event.target.checked;

          setEditedRow(editedRow);
          editing.onCellChange?.(editedRow.id, editedRow);
        },
        key: `${column.field}-${row.id}-editInput`,
        name: `${column.field}-${row.id}-editInput`,
        id: `${column.field}-${row.id}-editInput`,
      },
    );
  }, [column, editedRow, editing, index, row.id, setEditedRow]);

  const renderCell = useCallback((): JSX.Element | React.ReactNode => {
    const value = row[column.field];

    if (column.hideContentsforGroupHeader && isGroupHeaderCell) return null;

    if (editedRow.index === index && editing) {
      if (column.editCell && index === editedRow.index) {
        return renderEditCell();
      }
      if (column.renderCell) {
        return column.renderCell(value, index, column, row, {
          isParentRow: isGroupHeaderCell,
        });
      }
      return value;
    }
    return (
      column.renderCell?.(value, index, column, row, {
        isParentRow: isGroupHeaderCell,
      }) ?? value
    );
  }, [
    column,
    editedRow.index,
    editing,
    index,
    renderEditCell,
    row,
    isGroupHeaderCell,
  ]);

  const testID = isGroupHeaderCell
    ? `group-${column.field}-row-${index}-cell-${cellIndex}`
    : `${column.field}-row-${index}-cell-${cellIndex}`;

  return (
    <MUITableCell
      data-testid={testID}
      className={classNames(
        cellClasses.cell,
        classes?.rowCellClass?.(row.header ?? row, index, row[column.field]),
        column.isSticky && cellClasses.stickyCell,
        column.isSticky && isGroupHeaderCell && cellClasses.stickyHeaderCell,
        isLastSticky() && cellClasses.lastStickyCell,
        className,
      )}
      style={{
        textAlign: mapAlignmentToTextAlign(column.alignContents),
        verticalAlign: mapAlignmentToVerticalAlign(
          column.verticalAlignContents,
        ),
        width: computeColumnWidth(
          column,
          columnTotals ?? { totalFlex: 0, totalFixedPx: 0 },
          minTableWidth,
        ),
        left: getStickyLeft(),
      }}
    >
      {children || renderCell()}
    </MUITableCell>
  );
};

TableCell.defaultProps = {
  className: null,
  children: null,
  isGroupHeaderCell: false,
};

export default TableCell;
